import type { ModelCatalogItem } from "../gen/models";

export type ReasoningType =
  | "deep_reasoning"
  | "reasoning"
  | "hybrid_reasoning"
  | "standard";

export type IntegrationRoute = "google_direct" | "openrouter";

export interface ModelIntelligence {
  id: string;
  name: string;
  integrationRoute: IntegrationRoute;
  integrationLabel: string;
  underlyingProvider: string;
  contextLength: number;
  formattedContext: string;
  promptPrice: number;
  completionPrice: number;
  formattedPrice: string;
  isReasoning: boolean;
  reasoningType: ReasoningType;
  reasoningLabel: string;
  reasoningDescription: string;
  isCurated: boolean;
  supportsTools: boolean;
  supportsStructuredOutputs: boolean;
}

export function formatContextLength(tokens?: number): string {
  if (!tokens || tokens <= 0) return "128k tokens";
  if (tokens >= 1_000_000) {
    const m = (tokens / 1_000_000).toFixed(tokens % 1_000_000 === 0 ? 0 : 1);
    return `${m}M tokens`;
  }
  if (tokens >= 1_000) {
    return `${Math.round(tokens / 1_000)}k tokens`;
  }
  return `${tokens} tokens`;
}

export function resolveProviderArchitecture(
  id: string,
  rawProvider?: string
): {
  integrationRoute: IntegrationRoute;
  integrationLabel: string;
  underlyingProvider: string;
} {
  const cleaned = (id || "").trim();
  const isOpenRouter =
    cleaned.startsWith("openrouter:") ||
    cleaned.includes("/") ||
    rawProvider?.toLowerCase() === "openrouter";

  if (!isOpenRouter) {
    return {
      integrationRoute: "google_direct",
      integrationLabel: "Google (Direct API)",
      underlyingProvider: rawProvider || "Google",
    };
  }

  const modelPath = cleaned.replace(/^openrouter:/, "");
  const lab = modelPath.includes("/") ? modelPath.split("/")[0] : rawProvider || "OpenRouter";

  return {
    integrationRoute: "openrouter",
    integrationLabel: "OpenRouter",
    underlyingProvider: lab,
  };
}

export function classifyModelReasoning(modelOrId: string, catalogModels?: ModelCatalogItem[]) {
  const intel = getModelIntelligence(modelOrId, catalogModels);
  return {
    isReasoning: intel.isReasoning,
    reasoningType: intel.reasoningType,
    reasoningLabel: intel.reasoningLabel,
    reasoningDescription: intel.reasoningDescription,
  };
}

export function getModelIntelligence(
  modelOrId: ModelCatalogItem | string,
  catalogModels?: ModelCatalogItem[]
): ModelIntelligence {
  const item: Partial<ModelCatalogItem> =
    typeof modelOrId === "string"
      ? catalogModels?.find((m) => m.id === modelOrId) || {
          id: modelOrId,
          name: modelOrId,
        }
      : modelOrId;

  const id = item.id || (typeof modelOrId === "string" ? modelOrId : "gemini-3.8-flash");
  const name = item.name || id;

  const providerArch = resolveProviderArchitecture(id, item.provider);
  const contextLength = item.context_length || 128000;
  const promptPrice = item.prompt_price_per_mtok ?? 0;
  const completionPrice = item.completion_price_per_mtok ?? 0;
  const isCurated = Boolean(item.is_curated);
  const supportsTools = item.supports_tools !== false;
  const supportsStructuredOutputs = Boolean(item.supports_structured_outputs);
  const isReasoning = Boolean(item.is_reasoning);

  const formattedContext = formatContextLength(contextLength);
  const formattedPrice =
    promptPrice === 0 && completionPrice === 0
      ? "Free"
      : `$${promptPrice.toFixed(2)} in / $${completionPrice.toFixed(2)} out`;

  return {
    id,
    name,
    integrationRoute: providerArch.integrationRoute,
    integrationLabel: providerArch.integrationLabel,
    underlyingProvider: providerArch.underlyingProvider,
    contextLength,
    formattedContext,
    promptPrice,
    completionPrice,
    formattedPrice,
    isReasoning,
    reasoningType: isReasoning ? "reasoning" : "standard",
    reasoningLabel: isReasoning ? "Reasoning" : "Standard",
    reasoningDescription: isReasoning
      ? "Internal chain-of-thought deliberation prior to producing the final response."
      : "Direct low-latency token generation.",
    isCurated,
    supportsTools,
    supportsStructuredOutputs,
  };
}
